import { BufferGeometry, BufferAttribute } from 'three';
import MeshWriter, { Attr, AttrMap } from './mesh-writer';

/**
 * A ThreeJSMesh is a MeshWriter that sources mesh data from a
 * BufferGeometry instance. It also provides a static method to create a
 * BufferGeometry from generated mesh data.
 * @param {BufferGeometry} geo - The BufferGeometry instance. This is
 * converted to non-indexed form (if required), otherwise the class retains a
 * copy of the given geometry. If you want to mutate the original, be aware that
 * that mutation will be observed when `attr` or `write` are called.
 */

export class ThreeJSMesh extends MeshWriter {
    geo: BufferGeometry;

    constructor(geo: BufferGeometry) {
        super();
        if (geo.index === null) {
            this.geo = geo;
        } else {
            this.geo = geo.toNonIndexed();
        }
    }

    /**
     * This implementation of the MeshWriter `attr` interface method produces
     * values that represent the underlying BufferGeometry.
     */
    get attrs() {
        const attrSet: Set<Attr> = new Set();
        for (const attrName of Object.keys(this.geo.attributes)) {
            const tAttr = this.geo.getAttribute(attrName);
            const size = tAttr.itemSize;
            const count = tAttr.count;
            const type = Object.getPrototypeOf(tAttr.array).constructor;
            attrSet.add(new Attr(attrName, size, count, type));
        }
        return attrSet;
    }

    write(attrMap: AttrMap) {
        for (const [attr, data] of attrMap.entries()) {
            const tAttr = this.geo.getAttribute(attr.name);
            data.set(tAttr.array);
        }
    }

    /**
     * Transforms an attribute mapping, the sort of map returned from
     * MeshWriter.generate(), into a three.js BufferGeometry instance.
     */
    static toBufferGeometry(attrMap: AttrMap) {
        const geo = new BufferGeometry();
        for (const [attr, data] of attrMap.entries()) {
            const tAttr = new BufferAttribute(data, attr.size);
            geo.setAttribute(attr.name, tAttr);
        }
        return geo;
    }
}
